import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Scissors, Check, Loader2, Clock, MapPin, Phone, DollarSign, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { cn } from "@/lib/utils";
import { publicBookingAPI } from "@/lib/api";

// Utility functions
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
        style: 'currency',
        currency: 'GHS'
    }).format(amount || 0);
};

const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
};

export default function PublicBookingPage() {
    const { salonId } = useParams();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [salonInfo, setSalonInfo] = useState(null);
    const [services, setServices] = useState([]);
    const [staff, setStaff] = useState([]);
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        email: "",
        service_id: "",
        staff_id: null,
        date: addDays(new Date(), 1),
        time: "",
        notes: ""
    });

    const selectedService = services.find(s => s.id === formData.service_id);

    // Calculate booking fee
    const bookingFee = salonInfo?.booking_fee_enabled && selectedService
        ? (salonInfo.use_percentage_fee
            ? (selectedService.price * salonInfo.booking_fee_percentage / 100)
            : salonInfo.booking_fee_amount)
        : 0;

    const requiresPayment = salonInfo?.booking_fee_enabled && salonInfo?.paystack_enabled && bookingFee > 0;

    useEffect(() => {
        loadSalonInfo();
    }, [salonId]);

    useEffect(() => {
        if (formData.service_id && formData.date) {
            loadAvailableSlots();
        }
    }, [formData.service_id, formData.date, formData.staff_id]);

    const loadSalonInfo = async () => {
        try {
            // Load salon info
            const salonRes = await publicBookingAPI.getSalon(salonId);
            console.log('Salon API Response:', salonRes.data);
            
            // Backend returns: { success: true, salon: {...} }
            const salonData = salonRes.data.salon || salonRes.data.data || salonRes.data;
            console.log('Extracted Salon Data:', salonData);
            setSalonInfo(salonData);
            
            // Load services and staff - backend returns both in one endpoint
            const servicesRes = await publicBookingAPI.getServices(salonId);
            console.log('Services API Response:', servicesRes.data);
            
            // Backend returns: { success: true, services: [...], staff: [...] }
            const servicesData = servicesRes.data.services || servicesRes.data.data || servicesRes.data;
            const staffData = servicesRes.data.staff || [];
            
            // Set services array
            const servicesArray = Array.isArray(servicesData) ? servicesData : [];
            console.log('Final Services Array:', servicesArray);
            setServices(servicesArray);
            
            // Set staff array
            const staffArray = Array.isArray(staffData) ? staffData : [];
            console.log('Final Staff Array:', staffArray);
            setStaff(staffArray);
            
        } catch (error) {
            toast.error("Failed to load salon information");
            console.error("Load error:", error);
            console.error("Error response:", error.response);
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableSlots = async () => {
        setLoadingSlots(true);
        try {
            const dateStr = format(formData.date, 'yyyy-MM-dd');
            const res = await publicBookingAPI.getAvailability(salonId, dateStr);
            console.log('Availability Response:', res.data);
            
            // Backend returns: { success: true, date: "...", slots: [{time: "...", available: true/false}] }
            const slotsData = res.data.slots || res.data.data || [];
            console.log('Slots Data:', slotsData);
            setSlots(Array.isArray(slotsData) ? slotsData : []);
        } catch (error) {
            console.error("Failed to load slots:", error);
            setSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    };

    const handleSubmit = async () => {
        // Validate required fields
        if (!formData.name || !formData.phone || !formData.service_id || !formData.time) {
            toast.error("Please fill in all required fields");
            return;
        }

        // Validate email if payment required
        if (requiresPayment && !formData.email) {
            toast.error("Email is required for payment confirmation");
            return;
        }

        // Validate email format
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            toast.error("Please enter a valid email address");
            return;
        }
        
        setSubmitting(true);
        try {
            const payload = {
                salon_id: salonId,
                service_id: formData.service_id,
                client_name: formData.name,
                client_phone: formData.phone,
                client_email: formData.email || undefined,
                staff_id: formData.staff_id || undefined,
                date: format(formData.date, 'yyyy-MM-dd'),
                time: formData.time,
                notes: formData.notes || undefined
            };

            console.log('Booking Payload:', payload);
            const res = await publicBookingAPI.createBooking(payload);
            console.log('Booking Response:', res.data);
            
            // Backend returns: { success: true, appointment: {...}, payment: {...} }
            const result = res.data;

            // Check if payment is required
            if (result.payment && result.payment.authorization_url) {
                // Store booking reference for callback
                sessionStorage.setItem('booking_reference', result.payment.reference);
                sessionStorage.setItem('booking_salon_id', salonId);
                
                // Redirect to Paystack
                toast.info("Redirecting to payment...");
                setTimeout(() => {
                    window.location.href = result.payment.authorization_url;
                }, 1000);
            } else {
                // No payment required - booking confirmed immediately
                toast.success("Appointment booked successfully!");
                
                // Reset form
                setFormData({
                    name: "",
                    phone: "",
                    email: "",
                    service_id: "",
                    staff_id: null,
                    date: addDays(new Date(), 1),
                    time: "",
                    notes: ""
                });
                setStep(1);
            }
        } catch (error) {
            console.error("Booking error:", error);
            
            let errorMessage = "Failed to book appointment";
            
            if (error.response?.data) {
                const errorData = error.response.data;
                // Handle different error response structures
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (typeof errorData.error === 'string') {
                    errorMessage = errorData.error;
                } else if (typeof errorData.message === 'string') {
                    errorMessage = errorData.message;
                } else if (typeof errorData.detail === 'string') {
                    errorMessage = errorData.detail;
                } else if (errorData.error && typeof errorData.error === 'object') {
                    // Handle error object with code and message
                    errorMessage = errorData.error.message || JSON.stringify(errorData.error);
                }
            } else if (error.message && typeof error.message === 'string') {
                errorMessage = error.message;
            }
            
            // Ensure errorMessage is always a string
            if (typeof errorMessage !== 'string') {
                errorMessage = "An error occurred while booking. Please try again.";
            }
            
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading salon information...</p>
                </div>
            </div>
        );
    }

    if (!salonInfo) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <Card className="max-w-md w-full">
                    <CardContent className="text-center py-12">
                        <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-lg font-semibold mb-2">Salon Not Found</p>
                        <p className="text-muted-foreground">This booking page is not available</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="bg-card border-b border-border sticky top-0 z-10 shadow-sm">
                <div className="max-w-2xl mx-auto px-4 py-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <Scissors className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="font-heading font-bold text-lg truncate">
                                {salonInfo?.business_name || 'Loading...'}
                            </h1>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                                {salonInfo?.address && (
                                    <span className="flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        <span className="truncate">{salonInfo.address}</span>
                                    </span>
                                )}
                                {salonInfo?.phone && (
                                    <span className="flex items-center gap-1">
                                        <Phone className="w-3 h-3" />
                                        {salonInfo.phone}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 py-8">
                {/* Progress Steps */}
                <div className="flex items-center justify-center gap-2 mb-8">
                    {[1, 2, 3].map((s) => (
                        <div key={s} className="flex items-center">
                            <div className={cn(
                                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all",
                                s < step ? "bg-primary text-primary-foreground" :
                                s === step ? "bg-primary text-primary-foreground ring-4 ring-primary/20" :
                                "bg-secondary text-muted-foreground"
                            )}>
                                {s < step ? <Check className="w-4 h-4" /> : s}
                            </div>
                            {s < 3 && <div className={cn("w-8 h-1 mx-1", s < step ? "bg-primary" : "bg-secondary")} />}
                        </div>
                    ))}
                </div>

                {/* Step 1: Select Service */}
                {step === 1 && (
                    <Card className="border-border/40 shadow-card">
                        <CardHeader>
                            <CardTitle className="font-heading">Choose a Service</CardTitle>
                            <CardDescription>Select the service you&apos;d like to book</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {services.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-muted-foreground">No services available at this time</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {services.map((service) => {
                                        const serviceFee = salonInfo.booking_fee_enabled
                                            ? (salonInfo.use_percentage_fee
                                                ? (service.price * salonInfo.booking_fee_percentage / 100)
                                                : salonInfo.booking_fee_amount)
                                            : 0;
                                        
                                        return (
                                            <div
                                                key={service.id}
                                                onClick={() => setFormData({ ...formData, service_id: service.id })}
                                                className={cn(
                                                    "p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md",
                                                    formData.service_id === service.id
                                                        ? "border-primary bg-accent ring-2 ring-primary/20"
                                                        : "hover:border-primary/50 hover:bg-accent/50"
                                                )}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <p className="font-medium">{service.name}</p>
                                                        {service.description && (
                                                            <p className="text-sm text-muted-foreground mt-1">{service.description}</p>
                                                        )}
                                                        {service.category && (
                                                            <Badge variant="secondary" className="mt-2 text-xs">
                                                                {service.category}
                                                            </Badge>
                                                        )}
                                                        <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                                                            <Clock className="w-3 h-3" />
                                                            {service.duration_minutes} minutes
                                                        </div>
                                                    </div>
                                                    <div className="text-right ml-4">
                                                        <p className="font-semibold text-primary text-lg">
                                                            {formatCurrency(service.price)}
                                                        </p>
                                                        {salonInfo.booking_fee_enabled && serviceFee > 0 && (
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                +{formatCurrency(serviceFee)} booking fee
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="flex justify-end pt-6">
                                <Button
                                    onClick={() => setStep(2)}
                                    disabled={!formData.service_id}
                                    size="lg"
                                    className="rounded-full px-8"
                                >
                                    Next
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Step 2: Select Date & Time */}
                {step === 2 && (
                    <Card className="border-border/40 shadow-card">
                        <CardHeader>
                            <CardTitle className="font-heading">Pick Date & Time</CardTitle>
                            <CardDescription>Choose when you&apos;d like to visit</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {staff.length > 0 && (
                                <div className="space-y-2">
                                    <Label>Preferred Staff (optional)</Label>
                                    <Select
                                        value={formData.staff_id || "any"}
                                        onValueChange={(v) => setFormData({ ...formData, staff_id: v === "any" ? null : v, time: "" })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Any available staff" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="any">Any available staff</SelectItem>
                                            {staff.map((s) => (
                                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label>Select Date</Label>
                                <Calendar
                                    mode="single"
                                    selected={formData.date}
                                    onSelect={(d) => d && setFormData({ ...formData, date: d, time: "" })}
                                    disabled={(date) => {
                                        // Get today at start of day (midnight)
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        
                                        // Disable if date is before today
                                        return date < today;
                                    }}
                                    className="rounded-xl border shadow-sm"
                                    modifiersClassNames={{
                                        disabled: "line-through"
                                    }}
                                    classNames={{
                                        day_disabled: "text-muted-foreground opacity-50 cursor-not-allowed hover:bg-transparent line-through decoration-2",
                                        day: "hover:bg-accent cursor-pointer"
                                    }}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Available Times for {format(formData.date, 'EEEE, MMM d')}</Label>
                                {loadingSlots ? (
                                    <div className="text-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                                        <p className="text-sm text-muted-foreground mt-2">Loading available times...</p>
                                    </div>
                                ) : slots.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-muted-foreground">No available times for this date</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-4 gap-2 max-h-[240px] overflow-y-auto p-2 rounded-lg border bg-muted/20">
                                        {slots.map((slot) => {
                                            const slotTime = typeof slot === 'string' ? slot : slot.time;
                                            const slotAvailable = typeof slot === 'string' ? true : slot.available !== false;
                                            
                                            // Check if this time has passed (only for today)
                                            const now = new Date();
                                            const selectedDate = formData.date;
                                            const isToday = selectedDate.toDateString() === now.toDateString();
                                            
                                            let isPastTime = false;
                                            if (isToday && slotTime) {
                                                // Parse the time (format: "09:00:00" or "09:00")
                                                const [hours, minutes] = slotTime.split(':').map(Number);
                                                const slotDateTime = new Date();
                                                slotDateTime.setHours(hours, minutes, 0, 0);
                                                isPastTime = slotDateTime < now;
                                            }
                                            
                                            const isDisabled = !slotAvailable || isPastTime;
                                            
                                            return (
                                                <Button
                                                    key={slotTime}
                                                    variant={formData.time === slotTime ? "default" : "outline"}
                                                    size="sm"
                                                    disabled={isDisabled}
                                                    onClick={() => setFormData({ ...formData, time: slotTime })}
                                                    className={cn(
                                                        "transition-all",
                                                        isDisabled && "opacity-40 cursor-not-allowed line-through"
                                                    )}
                                                >
                                                    {formatTime(slotTime)}
                                                </Button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-between pt-4">
                                <Button variant="outline" onClick={() => setStep(1)} size="lg">
                                    Back
                                </Button>
                                <Button
                                    onClick={() => setStep(3)}
                                    disabled={!formData.time}
                                    size="lg"
                                    className="rounded-full px-8"
                                >
                                    Next
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Step 3: Your Details & Confirmation */}
                {step === 3 && (
                    <Card className="border-border/40 shadow-card">
                        <CardHeader>
                            <CardTitle className="font-heading">Your Details</CardTitle>
                            <CardDescription>Enter your contact information</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Full Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="John Doe"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="phone">Phone Number *</Label>
                                <Input
                                    id="phone"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    placeholder="+233 20 000 0000"
                                    type="tel"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="email">
                                    Email {requiresPayment && <span className="text-destructive">*</span>}
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="your@email.com"
                                />
                                {requiresPayment && (
                                    <p className="text-xs text-muted-foreground">
                                        Required for payment receipt and booking confirmation
                                    </p>
                                )}
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="notes">Special Requests (optional)</Label>
                                <Textarea
                                    id="notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="Any special requirements or preferences..."
                                    rows={3}
                                />
                            </div>

                            {/* Booking Fee Notice */}
                            {requiresPayment && (
                                <Alert className="border-amber-200 bg-amber-50">
                                    <DollarSign className="h-4 w-4 text-amber-600" />
                                    <AlertDescription className="text-sm">
                                        <p className="font-semibold text-amber-900 mb-1">Booking Fee Required</p>
                                        <p className="text-amber-800">
                                            A booking fee of <span className="font-bold">{formatCurrency(bookingFee)}</span> is required to secure your appointment.
                                            You&apos;ll be redirected to Paystack for secure payment.
                                        </p>
                                    </AlertDescription>
                                </Alert>
                            )}

                            {/* Booking Summary */}
                            <div className="p-4 rounded-xl bg-accent/50 space-y-3 mt-6">
                                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                                    Booking Summary
                                </p>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Service</span>
                                        <span className="font-medium">{selectedService?.name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Date</span>
                                        <span className="font-medium">{format(formData.date, 'EEE, MMM d, yyyy')}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Time</span>
                                        <span className="font-medium">{formatTime(formData.time)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Duration</span>
                                        <span className="font-medium">{selectedService?.duration_minutes} mins</span>
                                    </div>
                                    <div className="border-t pt-2 mt-2">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Service Price</span>
                                            <span className="font-semibold">{formatCurrency(selectedService?.price || 0)}</span>
                                        </div>
                                        {requiresPayment && (
                                            <div className="flex justify-between text-amber-700 mt-1">
                                                <span>Booking Fee (Pay Now)</span>
                                                <span className="font-bold">{formatCurrency(bookingFee)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-between pt-6">
                                <Button variant="outline" onClick={() => setStep(2)} size="lg">
                                    Back
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    disabled={
                                        submitting || 
                                        !formData.name || 
                                        !formData.phone || 
                                        (requiresPayment && !formData.email)
                                    }
                                    size="lg"
                                    className="rounded-full px-8"
                                >
                                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    {requiresPayment ? 'Proceed to Payment' : 'Confirm Booking'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </main>
        </div>
    );
}